import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Space, Button, Table, Tag, Checkbox, Modal, Input, Popconfirm, Select, Empty, Alert, Tooltip, message } from 'antd';
import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { formatTS } from '../utils/time';
import { ExploreDrawer } from './ShiftSwapExplorePage';

// Oversight > Attendance & Schedule > Shift Swap Forum (prototype; menu
// entry for WFM Skills 3; routes/shift_swap.py): shifts people have
// published to swap, one row each. Each row shows how the viewer and the
// poster match both ways, without hiding anything; interested people go to their
// supervisor / WFM contact. Managers (WFM Skills 3) set Swap Approved or
// Canceled; the poster can delete their own entry.
const fmtShift = (x) => `${dayjs(x.Start).format('ddd MM/DD')} ${dayjs(x.Start).format('h:mm A')} – ${dayjs(x.End).format('h:mm A')}`;
const FIT_COLOR = { yes: 'green', partly: 'gold', no: 'red' };
// Can you work their shift: how it sits in the viewer's own availability.
const FIT_TEXT = {
  yes: 'Fits your availability',
  partly: 'Partly fits your availability',
  no: 'Outside your availability',
  unknown: 'Your availability is not entered for that day',
};
// Each of the viewer's shifts: how it sits in the poster's availability (hover).
const POSTER_FIT_TEXT = {
  yes: 'Fits their availability',
  partly: 'Partly fits their availability',
  no: 'Outside their availability',
  unknown: 'Their availability is not entered for that day',
};
// Expired: still Open when its shift day passed (shown under Show closed).
const STATUS_COLOR = { Open: 'blue', 'Swap Approved': 'green', Canceled: 'default', Expired: 'orange' };
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const shortTime = (t) => {
  const d = dayjs(`2000-01-01T${t}`);
  return d.format(d.minute() ? 'h:mma' : 'ha');
};

// The poster's WFM Availability as a short Mon-Sun list, the offered day in
// bold, with any day notes.
const WeekList = ({ days, offered }) => {
  const byDay = Object.fromEntries((days || []).map((d) => [d.Day, d]));
  if (!(days || []).length) return <Typography.Text type="secondary" style={{ fontSize: 13 }}>Not entered</Typography.Text>;
  return (
    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
      {WEEK.map((w, i) => {
        const d = byDay[i + 1];
        const text = !d ? 'Not set' : d.Available && d.Start && d.End ? `${shortTime(d.Start)}–${shortTime(d.End)}` : 'Not available';
        return (
          <div key={w} style={i + 1 === offered ? { fontWeight: 700 } : undefined}>
            <Typography.Text type={d?.Available ? 'success' : 'secondary'} strong={i + 1 === offered} style={{ fontSize: 12 }}>
              {w} {text}
            </Typography.Text>
            {d?.Notes && <Typography.Text type="secondary" style={{ fontSize: 11 }}> · {d.Notes}</Typography.Text>}
          </div>
        );
      })}
    </div>
  );
};

export default function ShiftSwapForumPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ posts: [], is_manager: false });
  const [loading, setLoading] = useState(true);
  const [closed, setClosed] = useState(false);
  const [statusFor, setStatusFor] = useState(null);
  // Show Swap Matches: the poster's own entry, or any entry for a manager.
  const [matchesFor, setMatchesFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/shift-swap/posts', { params: closed ? { closed: 1 } : {} }).then((r) => setData(r.data))
      .catch(() => { setData({ posts: [], is_manager: false }); message.error('Failed to load the forum.'); })
      .finally(() => setLoading(false));
  }, [closed]);
  useEffect(() => { load(); }, [load]);

  const remove = (p) => api.delete(`/api/shift-swap/posts/${p.ID}`)
    .then(() => { message.success('Deleted.'); load(); })
    .catch((e) => message.error(e.response?.data?.error || 'Could not delete it.'));

  const columns = [
    { title: 'Status', key: 'status', width: 110,
      render: (_, p) => (
        <div>
          <Tag color={STATUS_COLOR[p.Status]}>{p.Status}</Tag>
          {p.Status !== 'Open' && (
            <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {p.StatusByName ? `by ${p.StatusByName}` : ''}{p.StatusAt ? ` ${formatTS(p.StatusAt, 'MM/DD/YYYY h:mm A')}` : ''}
              {p.StatusNote ? ` · ${p.StatusNote}` : ''}
            </Typography.Text></div>
          )}
          {p.Status === 'Swap Approved' && p.AbsenceID && <AbsenceReminder style={{ marginTop: 6 }} />}
        </div>
      ) },
    // Open: soonest first; closed: newest first (as the API sends them).
    { title: 'Shift offered', key: 'shift', width: 270, defaultSortOrder: closed ? 'descend' : 'ascend',
      sorter: (a, b) => a.Start.localeCompare(b.Start),
      render: (_, p) => (
        <div>
          <Typography.Text strong>{dayjs(p.Start).format('ddd MM/DD')}</Typography.Text>
          <div>{dayjs(p.Start).format('h:mm A')} – {dayjs(p.End).format('h:mm A')}</div>
          <Typography.Text style={{ fontSize: 13 }}>{p.Department}{p.Hours != null ? ` · ${p.Hours} hrs` : ''}</Typography.Text>
          {p.AbsenceID && <div><Typography.Text style={{ fontSize: 13 }}>(absence notification)</Typography.Text></div>}
          {p.AbsenceFlag && (
            <div><Tag color="red" style={{ marginTop: 4 }}>
              {p.AbsenceFlag === 'canceled' ? 'Absence canceled' : 'Absence no longer covers this shift'}
            </Tag></div>
          )}
          {/* The part of the shift the absence takes. */}
          {p.NeedStart && (
            <div><Typography.Text strong style={{ fontSize: 13 }}>
              Needs cover {p.NeedStart === p.Start && p.NeedEnd === p.End ? 'the entire shift'
                : `${dayjs(p.NeedStart).format('h:mm A')} – ${dayjs(p.NeedEnd).format('h:mm A')} (${p.NeedHours} hrs)`}
            </Typography.Text></div>
          )}
        </div>
      ) },
    { title: 'Offered by', key: 'who', width: 200,
      sorter: (a, b) => (a.Name || '').localeCompare(b.Name || ''),
      render: (_, p) => (
        <div>
          <Typography.Text strong>{p.Name}</Typography.Text>
          <div><Typography.Text style={{ fontSize: 13 }}>{[p.TeamName, p.JobName].filter(Boolean).join(' / ')}</Typography.Text></div>
          {p.Note && <div><Typography.Text style={{ fontSize: 13 }}>Note: {p.Note}</Typography.Text></div>}
        </div>
      ) },
    { title: 'Their availability', key: 'avail', width: 190,
      render: (_, p) => <WeekList days={p.Availability} offered={dayjs(p.Start).day() || 7} /> },
    // First: can the poster take one of the viewer's shifts (colour key on hover).
    { title: (
      <Tooltip title="Green: fits their availability · Orange: partly fits · Red: outside it">
        <span style={{ cursor: 'help' }}>Your shifts you could propose they take <QuestionCircleOutlined /></span>
      </Tooltip>
    ), key: 'yours', width: 260,
      onCell: (p) => (p.Mine ? { colSpan: 2 } : {}),
      render: (_, p) => (p.Mine ? <Typography.Text type="secondary">Your post</Typography.Text>
        : p.YourShifts?.length ? (
          <Space size={[4, 4]} wrap>
            {p.YourShifts.map((y) => (
              <Tag key={y.Start} color={FIT_COLOR[y.PosterFit]} title={POSTER_FIT_TEXT[y.PosterFit] || POSTER_FIT_TEXT.unknown}>
                {fmtShift(y)} · {y.Department}
              </Tag>
            ))}
          </Space>
        ) : <Typography.Text type="secondary" style={{ fontSize: 13 }}>You have no other shifts in the next 21 days.</Typography.Text>) },
    { title: 'Can you work their shift?', key: 'yourFit', width: 170,
      onCell: (p) => (p.Mine ? { colSpan: 0 } : {}),
      render: (_, p) => (p.Mine ? null
        : <Tag color={FIT_COLOR[p.YourFit?.Fit]}>{FIT_TEXT[p.YourFit?.Fit] || FIT_TEXT.unknown}</Tag>) },
    { title: '', key: 'actions', width: 160,
      render: (_, p) => (
        <Space direction="vertical" size={4}>
          {(p.Mine || data.is_manager) && p.Status === 'Open' && (
            <Button size="small" type="primary" onClick={() => setMatchesFor(p)}>Show Swap Matches</Button>
          )}
          {data.is_manager && p.Status === 'Open' && (
            <Button size="small" onClick={() => setStatusFor(p)}>Set status</Button>
          )}
          {/* Not an absence notification's entry (a manager posted it; Set status closes it). */}
          {p.Mine && (!p.AbsenceID || data.is_manager) && (
            <Popconfirm title="Delete this entry from the forum?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(p)}>
              <Button size="small" danger>Delete</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ];

  return (
    <>
      {/* Title, then filters; the create button on the far right. */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Shift Swap Forum</Typography.Title>
        <Checkbox checked={closed} onChange={(e) => setClosed(e.target.checked)}>Show closed</Checkbox>
        <Button type="primary" icon={<SearchOutlined />} style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/shift-swap-forum/explore')}>
          Explore Possible Swap
        </Button>
      </div>
      <Table key={closed ? 'closed' : 'open'} size="small" rowKey="ID" loading={loading} dataSource={data.posts} columns={columns}
        pagination={false} scroll={{ x: 1360 }}
        locale={{ emptyText: <Empty description={closed ? 'No closed entries.' : 'No shifts on the forum.'} /> }} />
      <ExploreDrawer data={matchesFor ? { person: { DisplayName: matchesFor.Name }, is_manager: data.is_manager } : null}
        shift={matchesFor ? { Start: matchesFor.Start, End: matchesFor.End, Department: matchesFor.Department, PostID: matchesFor.ID } : null}
        userId={matchesFor?.UserID} onClose={() => setMatchesFor(null)} onPublish={() => {}} />
      <StatusModal post={statusFor} onClose={() => setStatusFor(null)} onSaved={() => { setStatusFor(null); load(); }} />
    </>
  );
}

// An approved swap of a shift posted from an absence notification: the
// notification still stands until the person cancels or changes it.
const AbsenceReminder = ({ style }) => (
  <Alert type="warning" showIcon style={style}
    message="Please cancel the absence notification or update the times based on the swap agreement" />
);

// A manager's Swap Approved (note: who it was swapped with) or Canceled.
function StatusModal({ post, onClose, onSaved }) {
  const [status, setStatus] = useState('Swap Approved');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatus('Swap Approved'); setNote(''); }, [post]);
  const save = () => {
    setSaving(true);
    api.put(`/api/shift-swap/posts/${post.ID}/status`, { status, note })
      .then(() => { message.success('Saved.'); onSaved(); })
      .catch((e) => message.error(e.response?.data?.error || 'Could not save.'))
      .finally(() => setSaving(false));
  };
  return (
    <Modal open={!!post} onCancel={onClose} onOk={save} okText="Save" confirmLoading={saving} destroyOnClose
      title={post ? `${post.Name} · ${fmtShift(post)}` : ''}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select value={status} onChange={setStatus} style={{ width: 200 }}
          options={[{ value: 'Swap Approved', label: 'Swap Approved' }, { value: 'Canceled', label: 'Canceled' }]} />
        <Input.TextArea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={status === 'Swap Approved' ? 'Who it was swapped with' : 'Note'} />
        {status === 'Swap Approved' && post?.AbsenceID && <AbsenceReminder />}
      </Space>
    </Modal>
  );
}
